import {
  ComplianceStatus,
  ObligationRulesCategory,
  ObligationRulesFrequency,
  ObligationRulesGeneratorConfirmationDecisionType,
  ObligationRulesGeneratorMode,
} from '@commenda/prisma';
import { SWRFetcher } from '@commenda/utils';
import useSWR, { KeyedMutator } from 'swr';

/** A single calendar item surfaced for confirmation/preview. */
export type ObligationRulesGeneratorConfirmationItem = {
  id: string;
  filingCompositeId: string | null;
  title: string | null;
  effectiveYear: number | null;
  effectiveStartDate: string;
  effectiveEndDate: string;
  state: string | null;
  dueDate: string | null;
  status: ComplianceStatus | null;
  mode: ObligationRulesGeneratorMode | null;
  decision: ObligationRulesGeneratorConfirmationDecisionType | null;
  ruleDescription: string | null;
  category: ObligationRulesCategory | null;
  frequency: ObligationRulesFrequency | null;
};

export type ObligationRulesGeneratorConfirmationCalendar = {
  items: ObligationRulesGeneratorConfirmationItem[];
  count: number;
};

/**
 * Reads a corporation's calendar for a fiscal year with each item's rule-engine `mode`, the
 * customer's persisted confirm/deny decision
 */
export function useObligationRulesGeneratorConfirmationCalendar(
  token: string,
  params: { corporationId?: number; fiscalYear?: number } | null
) {
  const shouldFetch = params?.corporationId !== undefined;

  const queryParams = new URLSearchParams();
  if (shouldFetch) {
    queryParams.set('corporationId', String(params.corporationId));
    // fiscalYear is optional: omitting it reads across all fiscal years
    if (params.fiscalYear !== undefined) {
      queryParams.set('fiscalYear', String(params.fiscalYear));
    }
  }
  const url = shouldFetch
    ? `compliance/confirmation/calendar?${queryParams.toString()}`
    : null;

  const {
    data,
    error,
    isLoading,
    mutate,
  }: {
    data?: { calendar: ObligationRulesGeneratorConfirmationCalendar };
    error?: unknown;
    isLoading: boolean;
    mutate: KeyedMutator<unknown>;
  } = useSWR(url, SWRFetcher({ method: 'GET' }, token));

  return { data, error, isLoading, mutate };
}
